import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { UploadOutlined } from '@ant-design/icons';
import { Input, Select, Button, Row, Col, Typography, Space, Tag , Alert } from 'antd';
import { CreateSnippetRequest, UpdateSnippetRequest, UpdateSnippetResponse, SnippetEntity } from '@kemu-io/kemu-types';
import RoundedModal from '../roundedModal/roundedModal';
import FileDropZone from '../FileDropZone/FileDropZone';
import SvgFromString from '../SvgFromString/SvgFromString';
import KemuSwitch from '../form-control/kemuSwitch/KemuSwitch';
import MarkdownViewer from '../MarkdownViewer/MarkdownViewer';
import styles from './CreateSnippetModal.module.css';
import { selectCreateSnippetModal, showCreateSnippetModal, showPremiumUpgradeModal } from '@src/features/interface/interfaceSlice';
import { updateSnippetInResults, setSelectedSnippet, UISnippet, addSnippetToResults } from '@src/app/reducers/snippets/snippetsSlice';
import useTranslation from '@hooks/useTranslation';
import useKemuNotification from '@hooks/useKemuNotification';
import { createSnippet, getSnippetDetails, updateSnippet } from '@src/api/snippet/snippetApi';
import { SNIPPET_CATEGORIES, convertSnippetEntityToUI, decodeSvgFromBase64, encodeSvgToBase64 } from '@src/common/constants/snippetCategories';
import { decodeServerErrorInfo } from '@src/app/reducers/widget/utils';
import { SNIPPET_ICON_MAX_FILE_SIZE, SUBSCRIPTION_LIMIT_REACHED } from '@common/constants';
import { processSvgFile, SvgErrorKey } from '@components/gates/common';

const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;

/**
 * Modal component for creating and editing code snippets
 */
const CreateSnippetModal = (): React.JSX.Element => {
	const dispatch = useDispatch();
	const t = useTranslation('CreateSnippetModal');
	const cw = useTranslation('CommonWords');
	const svgValidation = useTranslation('SvgValidations');
	const kemuNotification = useKemuNotification();
	const modalData = useSelector(selectCreateSnippetModal);

	// Form state
	const [title, setTitle] = useState('');
	const [shortDescription, setShortDescription] = useState('');
	const [longDescription, setLongDescription] = useState('');
	const [category, setCategory] = useState('Snippets');
	const [githubUrl, setGithubUrl] = useState('');
	const [tags, setTags] = useState<string[]>([]);
	const [tagInput, setTagInput] = useState('');
	const [visibility, setVisibility] = useState<'Private' | 'Organization' | 'Public'>('Private');
	const [snippetCode, setSnippetCode] = useState('');
	const [iconFile, setIconFile] = useState<Uint8Array | null>(null);
	const [svgString, setSvgString] = useState('');
	const [fileLoaded, setFileLoaded] = useState(false);
	const [markdownPreview, setMarkdownPreview] = useState(false);
	const [loading, setLoading] = useState(false);
	const [loadingSnippet, setLoadingSnippet] = useState(false);
	const [error, setError] = useState<string | null>(null);
  const isEditing = !!modalData?.snippetId;

	/**
	 * Handles closing the modal
	 */
	const handleClose = useCallback(() => {
		dispatch(showCreateSnippetModal(null));
		// Reset form state
		setTitle('');
		setShortDescription('');
		setLongDescription('');
		setCategory('Snippets');
		setGithubUrl('');
		setTags([]);
		setTagInput('');
		setVisibility('Private');
		setSnippetCode('');
		setIconFile(null);
		setSvgString('');
		setFileLoaded(false);
		setMarkdownPreview(false);
		setLoading(false);
		setLoadingSnippet(false);
		setError(null);
	}, [dispatch]);

	/**
	 * Handles saving the snippet (create or update)
	 */
	const handleSave = useCallback(async () => {
		setLoading(true);
		setError(null);

    const rawIconSvg = iconFile ? String.fromCharCode(...iconFile) : svgString;
    const base64IconSvg = rawIconSvg ? encodeSvgToBase64(rawIconSvg) : '';

		try {
			// Convert visibility to lowercase to match API expectations
			const apiVisibility = visibility.toLowerCase() as 'private' | 'organization' | 'public';

			// Convert icon file to base64 string if present

			const isEditing = !!modalData?.snippetId;

			if (isEditing) {
				// Update existing snippet
				const updateData: UpdateSnippetRequest = {
					snippet: {
						title,
						shortDescription,
						descriptionMarkdown: longDescription,
						category,
						tags,
						visibility: apiVisibility,
						contents: snippetCode,
						iconSvg: base64IconSvg,
						githubUrl: githubUrl || undefined,
					}
				};

				const response: UpdateSnippetResponse = await updateSnippet(modalData.snippetId!, updateData);

				// Transform the updated snippet to UISnippet format
				const updatedSnippet = convertSnippetEntityToUI(response.snippet, rawIconSvg);

				// Update Redux state with the updated snippet
				dispatch(updateSnippetInResults({
					snippetId: response.snippet.id,
					updates: updatedSnippet,
				}));

				// Update selected snippet if it's the same one
				dispatch(setSelectedSnippet(updatedSnippet));

				// Show success notification
				kemuNotification({
					code: 'snippet-update',
					message: t('UpdateSuccess', 'Snippet updated successfully!'),
					type: 'success',
				});
			} else {
				// Create new snippet
				const snippetData: CreateSnippetRequest = {
					snippet: {
						title,
						shortDescription,
						descriptionMarkdown: longDescription,
						category,
						tags,
						visibility: apiVisibility,
						contents: snippetCode,
						iconSvg: base64IconSvg,
						githubUrl: githubUrl || undefined,
					}
				};

				const createdSnippet = await createSnippet(snippetData);
        const createdSnippetUI = convertSnippetEntityToUI(createdSnippet.snippet, rawIconSvg);

        dispatch(addSnippetToResults(createdSnippetUI));

				// Show success notification
				kemuNotification({
					code: 'snippet-create',
					message: t('CreateSuccess', 'Snippet created successfully!'),
					type: 'success',
				});
			}

			handleClose();
		} catch (err: any) {
			const { message, errorCode } = decodeServerErrorInfo(err);
			const errorMessage = isEditing ? 'Failed to update snippet' : 'Failed to create snippet';
			setError(message || errorMessage);
			if (errorCode === SUBSCRIPTION_LIMIT_REACHED) {
				dispatch(showPremiumUpgradeModal(true));
			}
		} finally {
			setLoading(false);
		}
	}, [title, shortDescription, longDescription, category, githubUrl, tags, visibility, snippetCode, iconFile, modalData?.snippetId, kemuNotification, t, handleClose, dispatch]);

	/**
	 * Handles file added from FileDropZone
	 * Validates file size, SVG content, and sanitizes the SVG before storing
	 */
	const handleFileAdded = useCallback((data: Uint8Array) => {
		const sanitizedSvg = processSvgFile(
			data,
			SNIPPET_ICON_MAX_FILE_SIZE,
			(errorKey) => {
				kemuNotification({
					code: 'snippet-icon-error',
					message: svgValidation(
						errorKey,
						errorKey === SvgErrorKey.FILE_SIZE
							? { maxSize: SNIPPET_ICON_MAX_FILE_SIZE / 1024 }
							: undefined
					),
					type: 'error',
				});
			}
		);

		if (!sanitizedSvg) {
			return;
		}

		// Convert sanitized SVG string to Uint8Array for storage
		const encoder = new TextEncoder();
		const svgContent = encoder.encode(sanitizedSvg);
		setIconFile(svgContent);
		setFileLoaded(true);
		setSvgString(sanitizedSvg);
	}, [svgValidation, kemuNotification]);

	/**
	 * Handles adding a tag
	 */
	const handleAddTag = useCallback(() => {
		const trimmedTag = tagInput.trim().toLowerCase();
		if (trimmedTag && !tags.includes(trimmedTag) && tags.length < 8) {
			setTags([...tags, trimmedTag]);
			setTagInput('');
		}
	}, [tagInput, tags]);

	/**
	 * Handles removing a tag
	 */
	const handleRemoveTag = useCallback((tagToRemove: string) => {
		setTags(tags.filter(tag => tag !== tagToRemove));
	}, [tags]);

	/**
	 * Handles tag input key press
	 */
	const handleTagInputKeyPress = useCallback((e: React.KeyboardEvent) => {
		if (e.key === 'Enter' || e.key === ',') {
			e.preventDefault();
			handleAddTag();
		}
	}, [handleAddTag]);

	/**
	 * Loads snippet data when editing an existing snippet
	 */
	const loadSnippetData = useCallback(async (snippetId: string) => {
		setLoadingSnippet(true);
		setError(null);

		try {
			const snippet = await getSnippetDetails({ snippetId, includeContents: true });

			// Populate form with snippet data
			setTitle(snippet.title);
			setShortDescription(snippet.shortDescription);
			setLongDescription(snippet.descriptionMarkdown);
			setCategory(snippet.category);
			setTags(snippet.tags);
			setVisibility(snippet.visibility.charAt(0).toUpperCase() + snippet.visibility.slice(1) as 'Private' | 'Organization' | 'Public');
			setSnippetCode(snippet.contents);
			setGithubUrl(snippet.githubUrl || '');

			// Handle icon if present
			if (snippet.iconSvg) {
				try {
          const decodedIconSvg = decodeSvgFromBase64(snippet.iconSvg);
					setIconFile(null);
					setSvgString(decodedIconSvg);
					setFileLoaded(true);
				} catch (iconError) {
					console.warn('Failed to decode snippet icon:', iconError);
				}
			}
		} catch (err: any) {
			setError(err.message || 'Failed to load snippet data');
		} finally {
			setLoadingSnippet(false);
		}
	}, []);

	/**
	 * Updates snippet code when modal data changes
	 */
	useEffect(() => {
		if (modalData?.snippetCode) {
			setSnippetCode(modalData.snippetCode);
		}
	}, [modalData?.snippetCode]);

	/**
	 * Load snippet data when modal opens with a snippet ID
	 */
	useEffect(() => {
		if (modalData?.snippetId) {
			loadSnippetData(modalData.snippetId);
		}
	}, [modalData?.snippetId, loadSnippetData]);

	const visible = modalData !== null;

	return (
		<RoundedModal
			visible={visible}
			onCancel={handleClose}
			title={isEditing ? t('EditTitle', 'Edit Snippet') : t('Title')}
			width={800}
			className={styles.ModalRoot}
			bodyClassName={styles.ModalBody}
			okBtnLabel={isEditing ? t('UpdateBtn', 'Update Snippet') : t('CreateBtn')}
			cancelBtnLabel={cw('Cancel')}
			onOk={handleSave}
			loading={loading || loadingSnippet}
			disableOkButton={!title.trim() || !shortDescription.trim() || !category || !visibility || !snippetCode.trim() || loading || loadingSnippet}
		>
			<div className={styles.Container}>
				<div className={styles.ModalContent}>
					{error && (
						<Alert
							message={error}
							type="error"
							showIcon
							closable
							onClose={() => setError(null)}
							style={{ marginBottom: 16 }}
						/>
					)}
					<Text type="secondary" className={styles.Subtitle}>
						{t('Subtitle')}
					</Text>

					<Row gutter={[24, 24]}>
						{/* Icon Upload Section */}
						<Col span={8}>
							<FileDropZone
								onFileAdded={handleFileAdded}
								accept=".svg"
								fileLoaded={fileLoaded}
								className={styles.FileDropZone}
								hoverClass={styles.FileDropZoneHover}
								noFileElement={
									<div className={styles.DropZoneContent}>
										<UploadOutlined className={styles.UploadIcon} />
										<Text className={styles.DropText}>{t('DragDropText')}</Text>
										<Text type="secondary" className={styles.OrText}>{t('OrText')}</Text>
										<Button type="dashed" size="small">
											{t('ChooseFileBtn')}
										</Button>
									</div>
								}
								yesFileElement={
									<div className={styles.FileLoadedContent}>
										<SvgFromString
											svgStr={svgString}
											width={48}
											height={48}
										/>
										<Text type="secondary" style={{ fontSize: 12, marginTop: 8 }}>
											Click to change
										</Text>
									</div>
								}
							/>
						</Col>

					{/* Title and Short Description */}
					<Col span={16}>
						<Space direction="vertical" size="middle" style={{ width: '100%' }}>
							<div>
								<Text strong>{t('TitleLabel')}</Text>
								<Input
									placeholder={t('TitlePlaceholder')}
									value={title}
									onChange={(e) => setTitle(e.target.value)}
									style={{ marginTop: 8 }}
								/>
							</div>
							<div>
								<Text strong>{t('ShortDescriptionLabel')}</Text>
								<Input
									placeholder={t('ShortDescriptionPlaceholder')}
									value={shortDescription}
									onChange={(e) => setShortDescription(e.target.value)}
									maxLength={200}
									style={{ marginTop: 8 }}
								/>
								<Space size="small" style={{ marginTop: 4 }}>
									<Text type="secondary" style={{ fontSize: 12 }}>
										{t('ShortDescriptionHelp')}
									</Text>
									<Text type="secondary" style={{ fontSize: 12 }}>
										{t('CharCountRemaining', { count: 200 - shortDescription.length })}
									</Text>
								</Space>
							</div>
						</Space>
					</Col>

					{/* Long Description */}
					<Col span={24}>
						<div>
							<div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
								<Text strong>{t('LongDescriptionLabel')}</Text>
								<Text type="secondary" style={{ fontSize: 12 }}>(Optional)</Text>
								<div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
									<Text type="secondary" style={{ fontSize: 12 }}>Markdown Preview</Text>
									<KemuSwitch
										size="small"
										checked={markdownPreview}
										onChange={(checked) => setMarkdownPreview(checked)}
									/>
								</div>
							</div>
							{markdownPreview ? (
								<div className={styles.MarkdownPreview}>
									<MarkdownViewer
										markdown={longDescription || '*No content to preview*'}
										docsUrl=""
                    disablePermalink
                    disableSnippets
									/>
								</div>
							) : (
								<TextArea
									placeholder={t('LongDescriptionPlaceholder')}
									value={longDescription}
									onChange={(e) => setLongDescription(e.target.value)}
									rows={6}
									style={{ marginTop: 8 }}
								/>
							)}
						</div>
					</Col>

					{!markdownPreview && (
						<>
							{/* Category and GitHub URL */}
							<Col span={12}>
								<div>
									<Text strong>{t('CategoryLabel')}</Text>
									<Select
										value={category}
										onChange={setCategory}
										popupClassName={styles.SelectDropdown}
										style={{ width: '100%', marginTop: 8 }}
										placeholder="Select category"
									>
										{SNIPPET_CATEGORIES.map(cat => (
											<Option key={cat} value={cat}>{cat}</Option>
										))}
									</Select>
								</div>
							</Col>
							<Col span={12}>
								<div>
									<Text strong>{t('GitHubUrlLabel')}</Text>
									<Text type="secondary" style={{ fontSize: 12, marginLeft: 4 }}>(Optional)</Text>
									<Input
										type="url"
										placeholder={t('GitHubUrlPlaceholder')}
										value={githubUrl}
										onChange={(e) => setGithubUrl(e.target.value)}
										style={{ marginTop: 8 }}
									/>
								</div>
							</Col>

							{/* Tags and Snippet Code */}
							<Col span={12}>
								<div>
									<Text strong>{t('TagsLabel')}</Text>
									<Text type="secondary" style={{ fontSize: 12, marginLeft: 4 }}>(Optional)</Text>
									<div style={{ marginTop: tags.length ? 8 : 0 }}>
										<Space wrap>
											{tags.map(tag => (
												<Tag
													key={tag}
													closable
													onClose={() => handleRemoveTag(tag)}
												>
													{tag}
												</Tag>
											))}
										</Space>
										<Input
											placeholder={tags.length >= 8 ? 'Maximum 8 tags reached' : t('TagsPlaceholder')}
											value={tagInput}
											onChange={(e) => setTagInput(e.target.value)}
											onKeyPress={handleTagInputKeyPress}
											onBlur={handleAddTag}
											disabled={tags.length >= 8}
											style={{ marginTop: 8 }}
										/>
									</div>
									<Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
										{t('TagsHelp')}
									</Text>
								</div>
							</Col>
							<Col span={12}>
								<div>
									<Text strong>{t('VisibilityLabel')}</Text>
									<Select
										value={visibility}
										onChange={setVisibility}
										popupClassName={styles.SelectDropdown}
										style={{ width: '100%', marginTop: 8 }}
									>
										<Option value="Private">{t('VisibilityPrivate')}</Option>
										<Option value="Organization">{t('VisibilityOrganization')}</Option>
										<Option value="Public">{t('VisibilityPublic')}</Option>
									</Select>
								</div>
							</Col>

							{/* Snippet Code */}
							{isEditing && (
								<Col span={24}>
									<div>
										<Text strong>{t('SnippetCodeLabel')}</Text>
										<TextArea
											placeholder={t('SnippetCodePlaceholder')}
											value={snippetCode}
											onChange={(e) => setSnippetCode(e.target.value)}
											className={styles.SnippetCode}
											// readOnly={!isEditing}
											rows={8}
											style={{ marginTop: 8, fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace' }}
										/>
										{/* {!isEditing && (
											<Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
												{t('SnippetCodeReadOnly', 'Snippet code is generated from your selection and cannot be edited')}
											</Text>
										)} */}
									</div>
								</Col>
							)}
						</>
					)}
					</Row>
				</div>
			</div>
		</RoundedModal>
	);
};

export default CreateSnippetModal;
